import React, { forwardRef, useContext } from 'react';
import { default as SlickSlider, LazyLoadTypes, Settings as SliderSettings } from 'react-slick';
import { SliderItem } from './_Item/SliderItem';
import { BreakpointsContext } from '@src/providers/Breakpoints';
import { IDefaultComponentProps } from '@src/models/props';
import { classnames } from '@src/utils/classnames';

import styled from './Slider.scss';
import { AnyType } from '@src/models/common';

export interface INestedComponents {
  Item: typeof SliderItem;
}

interface Settings extends Partial<Pick<SliderSettings, 'nextArrow' | 'prevArrow'>> {
  autoplay?: boolean;
  fade?: boolean;
  /** рисует кнопки "вправо" и "влево" */
  arrows?: boolean;
  /** рисует "точки" быстрого переключения между слайдами */
  dots?: boolean;
  swipe?: boolean;
  infinite?: boolean;
  adaptiveHeight?: boolean;
  slidesToShow?: number;
  slidesToScroll?: number;
  autoplaySpeed?: number;
  responsive?: ResponsiveObject[] | undefined;
  lazyLoad?: LazyLoadTypes;
  initialSlide?: number;
  handleSlideChange?: AnyType;

  beforeChange?(currentSlide: number, nextSlide: number): void;
  afterChange?: (index: number) => void;
}

export interface ResponsiveObject {
  breakpoint: number;
  settings: 'unslick' | Settings;
}

export interface NativeSlickSlider extends SlickSlider {}

export interface ISliderProps extends IDefaultComponentProps, Settings {}

/**
 * Компонент для создания каруселей
 *
 * @link https://react-slick.neostack.com/docs/example/center-mode
 * */
export const Slider = forwardRef<NativeSlickSlider, ISliderProps>(
  (
    {
      children,
      className,
      style,
      slidesToShow = 1,
      slidesToScroll = 1,
      autoplaySpeed = 7000,
      lazyLoad = 'ondemand',
      handleSlideChange,
      ...rest
    },
    ref,
  ) => {
    const { isMobile } = useContext(BreakpointsContext);

    const classes = classnames(styled.root, className, {
      [styled.isMobile]: isMobile,
    });

    return (
      <div className={classes} style={style}>
        <SlickSlider
          ref={ref}
          slidesToShow={slidesToShow}
          slidesToScroll={slidesToScroll}
          autoplaySpeed={autoplaySpeed}
          lazyLoad={lazyLoad}
          className="Slider"
          afterChange={handleSlideChange}
          {...rest}
        >
          {children}
        </SlickSlider>
      </div>
    );
  },
) as unknown as React.ForwardRefExoticComponent<ISliderProps & React.RefAttributes<NativeSlickSlider>> &
  INestedComponents;

Slider.Item = SliderItem;

Slider.displayName = 'Slider';
